import React, { useState, useEffect } from 'react';
import {
  Calendar, Clock, UtensilsCrossed, ChefHat, Save, X,
  Edit2, AlertCircle, Check, ChevronLeft, ChevronRight,
  CalendarDays, Home, ArrowLeft, ArrowRight, ShoppingCart, Book, Store, Settings
} from 'lucide-react';
import { ShoppingList } from './ShoppingList';
import {
  format, addDays, startOfWeek, endOfWeek, isToday,
  addWeeks, subWeeks, isSameDay, isFuture, isPast
} from 'date-fns';
import { apiClient } from '../utils/api';
import { toast } from 'react-hot-toast';
import { getVendors, FoodVendor } from '@/api/foodOrders';

interface MealOption {
  id: string;
  name: string;
  emoji: string;
  category: string;
  prep_time_minutes: number;
  tags: string[];
  recipe?: string;
  ingredients?: string[];
  vendor_id?: string;
  vendor_name?: string;
}

interface MealSlot {
  id: string;
  date: string;
  meal_type: string;
  selected_meal_id?: string;
  finalized_meal_id?: string;
  who_cooks?: string;
  cook_name?: string;
  notes?: string;
  meal?: MealOption;
}

interface MealPlanEditorProps {
  onClose?: () => void;
  onAddNewMeal?: () => void;
  onManageMeals?: () => void;
}

export const EnhancedMealPlanEditor: React.FC<MealPlanEditorProps> = ({ onClose, onAddNewMeal, onManageMeals }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [mealSlots, setMealSlots] = useState<MealSlot[]>([]);
  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);
  const [vendors, setVendors] = useState<FoodVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState<string | null>(null);
  const [selectedMealId, setSelectedMealId] = useState<string>('');
  const [selectedCook, setSelectedCook] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'week' | 'list' | 'shopping'>('week');
  const [showRecipe, setShowRecipe] = useState<string | null>(null);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 0 });
  const today = new Date();

  useEffect(() => {
    loadWeekData();
    loadMealOptions();
    loadFamilyMembers();
    loadVendors();
  }, [currentWeek]);

  const loadWeekData = async () => {
    try {
      setLoading(true);
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(weekEnd, 'yyyy-MM-dd');
      
      // First ensure slots exist for this week
      try {
        await apiClient.post('/api/meals/ensure-week-slots', null, {
          params: { start_date: start, end_date: end }
        });
      } catch (slotError) {
        // Could not ensure slots, continuing anyway
      }
      
      const response = await apiClient.get(`/api/meals/week-plan?start=${start}&end=${end}`);
      // Process week plan response
      
      // Transform the response to the format expected by the component
      // Backend returns: [{date: "2025-08-10", breakfast: {...}, lunch: {...}, dinner: {...}}, ...]
      // Component expects: [{date: "2025-08-10", meal_type: "dinner", ...}, ...]
      const slots = [];
      if (Array.isArray(response)) {
        response.forEach(dayData => {
          // Process day data
          ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
            const mealData = dayData[mealType];
            if (mealData) {
              const slot = {
                id: mealData.id,
                date: dayData.date,
                meal_type: mealType,
                selected_meal_id: mealData.meal_id,
                finalized_meal_id: mealData.meal_id,
                meal_name: mealData.meal_name,
                meal_emoji: mealData.meal_emoji,
                who_cooks: mealData.who_cooks,
                cook_name: mealData.cook_name,
                notes: mealData.notes,
                // Add the meal object directly for rendering
                meal: {
                  id: mealData.meal_id,
                  name: mealData.meal_name,
                  emoji: mealData.meal_emoji,
                  category: mealData.meal_category,
                  prep_time_minutes: mealData.prep_time
                }
              };
              // Slot created
              slots.push(slot);
            }
          });
        });
      }
      
      // Slots loaded
      setMealSlots(slots);
    } catch (error) {
      console.error('Failed to load week data:', error);
      toast.error('Failed to load meal plan');
    } finally {
      setLoading(false);
    }
  };

  const loadMealOptions = async () => {
    try {
      const response = await apiClient.get('/api/meals/options');
      setMealOptions(response || []);
    } catch (error) {
      console.error('Failed to load meal options:', error);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const response = await apiClient.get('/api/users');
      // Handle both array response and object with users array
      const users = Array.isArray(response) ? response : (response?.users || []);
      setFamilyMembers(users);
    } catch (error) {
      console.error('Failed to load family members:', error);
      setFamilyMembers([]); // Set empty array on error
    }
  };

  const loadVendors = async () => {
    try {
      const data = await getVendors(true); // Only active vendors
      setVendors(data || []);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      setVendors([]);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'prev') {
      setCurrentWeek(subWeeks(currentWeek, 1));
    } else if (direction === 'next') {
      setCurrentWeek(addWeeks(currentWeek, 1));
    } else {
      setCurrentWeek(new Date());
    }
  };

  const startEdit = (slot: MealSlot) => {
    setEditingSlot(slot.id);
    setSelectedMealId(slot.finalized_meal_id || slot.selected_meal_id || '');
    setSelectedCook(slot.who_cooks || '');
    setNotes(slot.notes || '');
  };

  const cancelEdit = () => {
    setEditingSlot(null);
    setSelectedMealId('');
    setSelectedCook('');
    setNotes('');
  };

  // Get or create a meal option for a vendor
  const getOrCreateVendorMealOption = async (vendorId: string): Promise<string | null> => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (!vendor) return null;

    // Check if we already have a meal option for this vendor
    const existingOption = mealOptions.find(m => m.vendor_id === vendorId);
    if (existingOption) return existingOption.id;

    // Create a new meal option for this vendor
    try {
      const response = await apiClient.post('/api/meals/options', {
        name: vendor.vendor_name,
        emoji: '🏪',
        category: vendor.vendor_type === 'restaurant' ? 'restaurant' : 'takeout',
        meal_type: 'dinner',
        vendor_id: vendorId,
        vendor_name: vendor.vendor_name,
        description: `Order from ${vendor.vendor_name}`
      });
      // Reload meal options to include the new one
      await loadMealOptions();
      return response?.id || response?.meal_option?.id;
    } catch (error) {
      console.error('Failed to create vendor meal option:', error);
      return null;
    }
  };

  const saveChanges = async (slotId: string) => {
    if (!selectedMealId) {
      toast.error('Please select a meal');
      return;
    }

    setSaving(true);
    try {
      let mealId = selectedMealId;

      // Handle vendor selection
      if (selectedMealId.startsWith('vendor:')) {
        const vendorId = selectedMealId.replace('vendor:', '');
        const vendorMealId = await getOrCreateVendorMealOption(vendorId);
        if (!vendorMealId) {
          toast.error('Failed to create meal for vendor');
          setSaving(false);
          return;
        }
        mealId = vendorMealId;
      }

      // Use the quick-edit endpoint for updating meals
      await apiClient.post(`/api/meals/quick-edit/${slotId}`, {
        meal_id: mealId,
        who_cooks: selectedCook || null,
        notes: notes || null
      });
      toast.success('Meal plan updated!');
      cancelEdit();
      loadWeekData();
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to update meal plan');
    } finally {
      setSaving(false);
    }
  };

  // Create or update a meal slot
  const handleSlotSave = async (date: string, mealType: string = 'dinner', existingSlotId?: string) => {
    if (!selectedMealId) {
      toast.error('Please select a meal');
      return;
    }

    setSaving(true);
    try {
      let slotId = existingSlotId;
      let mealId = selectedMealId;

      // Handle vendor selection
      if (selectedMealId.startsWith('vendor:')) {
        const vendorId = selectedMealId.replace('vendor:', '');
        const vendorMealId = await getOrCreateVendorMealOption(vendorId);
        if (!vendorMealId) {
          toast.error('Failed to create meal for vendor');
          setSaving(false);
          return;
        }
        mealId = vendorMealId;
      }

      // If no slot exists, create one first
      if (!slotId) {
        const createResult = await apiClient.post('/api/meals/create-slot', null, {
          params: { slot_date: date, meal_type: mealType }
        });
        slotId = createResult.slot_id;
      }

      // Now update the slot with the meal
      await apiClient.post(`/api/meals/quick-edit/${slotId}`, {
        meal_id: mealId,
        who_cooks: selectedCook || null,
        notes: notes || null
      });

      toast.success('Meal saved!');
      cancelEdit();
      loadWeekData();
    } catch (error) {
      console.error('Failed to save meal:', error);
      toast.error('Failed to save meal');
    } finally {
      setSaving(false);
    }
  };

  const getDaySlots = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return mealSlots.filter(slot => slot.date === dateStr);
  };

  const getMealOption = (mealId?: string) => {
    if (!mealId) return null;
    return mealOptions.find(m => m.id === mealId);
  };

  const getDayHighlight = (date: Date) => {
    if (isToday(date)) return 'ring-2 ring-blue-500 bg-blue-50';
    if (isPast(date)) return 'bg-gray-50';
    return 'bg-white';
  };

  const formatWeekRange = () => {
    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading meal plan...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header with Navigation */}
      <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold flex items-center">
            <UtensilsCrossed className="w-6 h-6 mr-2" />
            Weekly Meal Plan
          </h2>
          <div className="flex items-center gap-2">
            {onManageMeals && (
              <button
                onClick={onManageMeals}
                className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
                title="Manage Meals"
              >
                <Settings className="w-5 h-5" />
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Previous Week"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigateWeek('today')}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              <Home className="w-4 h-4 mr-1" />
              Today
            </button>
            <span className="font-medium">{formatWeekRange()}</span>
          </div>

          <button
            onClick={() => navigateWeek('next')}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Next Week"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'week'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarDays className="w-4 h-4 inline mr-1" />
            Week View
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            List View
          </button>
        </div>
      </div>

      {/* Week Grid View */}
      {viewMode === 'week' && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {[...Array(7)].map((_, index) => {
              const currentDate = addDays(weekStart, index);
              const daySlots = getDaySlots(currentDate);
              const dinnerSlot = daySlots.find(s => s.meal_type === 'dinner');
              // Use meal data directly from slot, or look it up from options
              const meal = dinnerSlot?.meal || getMealOption(dinnerSlot?.finalized_meal_id || dinnerSlot?.selected_meal_id);
              
              return (
                <div
                  key={index}
                  className={`border rounded-lg p-3 transition-all ${getDayHighlight(currentDate)}`}
                >
                  <div className="mb-2">
                    <div className="font-semibold text-sm text-gray-900">
                      {format(currentDate, 'EEE')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {format(currentDate, 'MMM d')}
                    </div>
                    {(meal || dinnerSlot?.meal_name) && (
                      <div className="mt-1 text-xs text-gray-700 font-medium truncate" title={meal?.name || dinnerSlot?.meal_name}>
                        {meal?.emoji || dinnerSlot?.meal_emoji || '🍽️'} {meal?.name || dinnerSlot?.meal_name}
                      </div>
                    )}
                    {isToday(currentDate) && (
                      <div className="mt-1">
                        <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                          Today
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {(editingSlot === dinnerSlot?.id || editingSlot === `new-${format(currentDate, 'yyyy-MM-dd')}-dinner`) ? (
                      <div className="space-y-2">
                        <select
                          id="meal-select-inline"
                          name="meal-select-inline"
                          value={selectedMealId}
                          onChange={(e) => {
                            if (e.target.value === '__add_new__' && onAddNewMeal) {
                              onAddNewMeal();
                            } else {
                              setSelectedMealId(e.target.value);
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border rounded"
                        >
                          <option value="">Select meal...</option>
                          {onAddNewMeal && (
                            <option value="__add_new__">+ Add New Meal...</option>
                          )}
                          {mealOptions.length > 0 && (
                            <optgroup label="Home Meals">
                              {mealOptions.map(option => (
                                <option key={option.id} value={option.id}>
                                  {option.emoji} {option.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {vendors.length > 0 && (
                            <optgroup label="Restaurants / Takeout">
                              {vendors.map(vendor => (
                                <option key={`vendor-${vendor.id}`} value={`vendor:${vendor.id}`}>
                                  🏪 {vendor.vendor_name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        <select
                          id="cook-select-inline"
                          name="cook-select-inline"
                          value={selectedCook}
                          onChange={(e) => setSelectedCook(e.target.value)}
                          className="w-full px-2 py-1 text-sm border rounded"
                        >
                          <option value="">Who's cooking?</option>
                          {familyMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.display_name}
                            </option>
                          ))}
                        </select>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              const dateStr = format(currentDate, 'yyyy-MM-dd');
                              handleSlotSave(dateStr, 'dinner', dinnerSlot?.id);
                            }}
                            disabled={saving}
                            className="flex-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                          >
                            <Check className="w-3 h-3 inline" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="flex-1 px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                          >
                            <X className="w-3 h-3 inline" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => dinnerSlot ? startEdit(dinnerSlot) : setEditingSlot(`new-${format(currentDate, 'yyyy-MM-dd')}-dinner`)}
                        className="cursor-pointer hover:bg-gray-50 rounded p-2 transition-colors"
                      >
                        {(meal || dinnerSlot?.meal_name) ? (
                          <div>
                            <div className="text-2xl text-center mb-1">{meal?.emoji || dinnerSlot?.meal_emoji || '🍽️'}</div>
                            <div className="text-xs font-medium text-gray-900 text-center truncate px-1" title={meal?.name || dinnerSlot?.meal_name}>
                              {meal?.name || dinnerSlot?.meal_name}
                            </div>
                            {dinnerSlot?.cook_name && (
                              <div className="text-xs text-gray-500 text-center mt-1">
                                <ChefHat className="w-3 h-3 inline mr-1" />
                                {dinnerSlot.cook_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-2">
                            <Edit2 className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                            <div className="text-xs text-gray-400">Click to plan</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="p-4">
          <div className="space-y-2">
            {[...Array(7)].map((_, index) => {
              const currentDate = addDays(weekStart, index);
              const daySlots = getDaySlots(currentDate);
              const dinnerSlot = daySlots.find(s => s.meal_type === 'dinner');
              // Use meal data directly from slot, or look it up from options
              const meal = dinnerSlot?.meal || getMealOption(dinnerSlot?.finalized_meal_id || dinnerSlot?.selected_meal_id);
              
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${getDayHighlight(currentDate)}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-center">
                      <div className="font-semibold text-sm">{format(currentDate, 'EEE')}</div>
                      <div className="text-xs text-gray-500">{format(currentDate, 'MMM d')}</div>
                      {isToday(currentDate) && (
                        <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full mt-1 inline-block">
                          Today
                        </span>
                      )}
                    </div>
                    
                    {(meal || dinnerSlot?.meal_name) ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{meal?.emoji || dinnerSlot?.meal_emoji || '🍽️'}</span>
                        <div>
                          <div className="font-medium text-sm">{meal?.name || dinnerSlot?.meal_name}</div>
                          {dinnerSlot?.cook_name && (
                            <div className="text-xs text-gray-500">
                              <ChefHat className="w-3 h-3 inline mr-1" />
                              {dinnerSlot.cook_name} cooking
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">
                        No meal planned
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => dinnerSlot ? startEdit(dinnerSlot) : setEditingSlot(`new-${format(currentDate, 'yyyy-MM-dd')}-dinner`)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-4 border-t bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            Click any day to edit or add meals
          </div>
          <button
            onClick={() => setViewMode('shopping')}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm flex items-center gap-1"
          >
            <ShoppingCart className="w-4 h-4" />
            Shopping List
          </button>
        </div>
      </div>

      {/* Edit Modal for List View */}
      {viewMode === 'list' && editingSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {editingSlot.startsWith('new-') ? 'Add Meal' : 'Edit Meal'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Meal
                </label>
                <select
                  id="meal-select-modal"
                  name="meal-select-modal"
                  value={selectedMealId}
                  onChange={(e) => {
                    if (e.target.value === '__add_new__' && onAddNewMeal) {
                      onAddNewMeal();
                    } else {
                      setSelectedMealId(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Choose a meal...</option>
                  {onAddNewMeal && (
                    <option value="__add_new__">+ Add New Meal...</option>
                  )}
                  {mealOptions.length > 0 && (
                    <optgroup label="Home Meals">
                      {mealOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.emoji} {option.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {vendors.length > 0 && (
                    <optgroup label="Restaurants / Takeout">
                      {vendors.map(vendor => (
                        <option key={`vendor-${vendor.id}`} value={`vendor:${vendor.id}`}>
                          🏪 {vendor.vendor_name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Who's Cooking?
                </label>
                <select
                  id="cook-select-modal"
                  name="cook-select-modal"
                  value={selectedCook}
                  onChange={(e) => setSelectedCook(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select cook...</option>
                  {familyMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    if (editingSlot.startsWith('new-')) {
                      const dateStr = editingSlot.replace('new-', '').replace('-dinner', '');
                      handleSlotSave(dateStr, 'dinner');
                    } else {
                      saveChanges(editingSlot);
                    }
                  }}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cancelEdit}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Shopping List View */}
      {viewMode === 'shopping' && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Shopping List</h2>
              <button
                onClick={() => setViewMode('week')}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Meal Plan
              </button>
            </div>
            <ShoppingList />
          </div>
        </div>
      )}
    </div>
  );
};